import React from 'react';

import { OperationVariables } from '@apollo/client';
import { Check as CheckIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  inputBaseClasses,
  TextField,
} from '@mui/material';
import _ from 'es-toolkit/compat';
import type { FieldInputProps } from 'react-final-form';

import type { GeneratedQueryHookType } from '@hrlogics/react-apollo-v3';

import { Nullable } from '@clearverify/types';

import { useAutocompleteFieldWithQuery } from 'src/hooks/forms';
import { SxProp } from 'src/types/mui';

import { getTextInputPlaceholder as getAutocompletePlaceholder } from './utils';

interface WithId {
  id?: string;
}

type DashboardFilterCVAdminAutocompleteProps<T extends WithId> = {
  input: FieldInputProps<T[]>;
  useQuery: GeneratedQueryHookType;
  getQueryItems: (data: Nullable<ReturnType<GeneratedQueryHookType>['data']>) => T[];
  queryFilter: (searchText: string) => OperationVariables;
  getOptionLabel: (option: any) => string;
  disabled?: boolean;
  width?: string | number;
  label: string;
};

export const DashboardFilterCVAdminAutocomplete = <T extends WithId>({
  input,
  useQuery,
  getQueryItems,
  queryFilter,
  getOptionLabel,
  disabled = false,
  width,
  label,
}: DashboardFilterCVAdminAutocompleteProps<T>) => {
  const { value, onChange } = input;

  const {
    options,
    getOptionLabel: getAutocompleteOptionLabel,
    loading,
    onSearch,
  } = useAutocompleteFieldWithQuery(
    useQuery,
    {
      getQueryItems,
      queryFilter,
    },
    { getOptionLabel },
  );

  const [inputValue, setInputValue] = React.useState<string>('');

  const onChangeValue = React.useCallback(
    (_event: React.SyntheticEvent, newValue: T[]) => {
      onChange(newValue);
    },
    [onChange],
  );

  const getOptionLabelForAutoComplete = React.useCallback(
    (option: T) => {
      if (getAutocompleteOptionLabel) return getAutocompleteOptionLabel(option);
      return String(option);
    },
    [getAutocompleteOptionLabel],
  );

  const onInputChange = React.useCallback(
    (_event: React.SyntheticEvent, inputValue: string) => {
      setInputValue(inputValue);
      onSearch?.(inputValue);
    },
    [onSearch],
  );

  const isOptionEqualToValue = React.useCallback((option: T, value: T) => {
    if (_.has(option, 'id') && _.has(value, 'id')) {
      return option.id === value.id;
    }

    return _.isEqual(option, value);
  }, []);

  const optionsWithValue = loading ? [] : _.uniqBy([...value, ...options], 'id');

  return (
    <FormControl variant="outlined" sx={{ width }}>
      <Autocomplete<T, true, false>
        multiple
        disabled={disabled}
        options={optionsWithValue}
        value={value}
        onChange={onChangeValue}
        inputValue={inputValue}
        onInputChange={onInputChange}
        disableCloseOnSelect
        isOptionEqualToValue={isOptionEqualToValue}
        loading={loading}
        loadingText={loading ? 'Loading...' : undefined}
        getOptionLabel={getOptionLabelForAutoComplete}
        disableClearable={false}
        noOptionsText="No options available"
        renderTags={value => {
          return (
            <Box sx={{ flexShrink: 0, ml: 1, pointerEvents: 'none' }}>
              {getAutocompletePlaceholder(value)}
            </Box>
          );
        }}
        renderInput={params => {
          return (
            <TextField
              {...params}
              placeholder={!value[0] ? getAutocompletePlaceholder(value) : ''}
              label={label}
              variant="outlined"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="secondary" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                },
              }}
              onKeyDown={event => {
                if (event.key === 'Backspace') {
                  event.stopPropagation();
                }
              }}
            />
          );
        }}
        sx={autocompleteStyles}
        renderOption={(props, option, state, ownerState) => {
          return (
            <li {...props} key={option.id}>
              {ownerState.getOptionLabel(option)}
              {state.selected ? <CheckIcon sx={{ ml: 'auto' }} /> : null}
            </li>
          );
        }}
        size="small"
      />
    </FormControl>
  );
};

const autocompleteStyles: SxProp = {
  [`& .${inputBaseClasses.root}`]: {
    flexWrap: 'nowrap',
  },
};
